import { Box, Text } from "@chakra-ui/react";
import { useEffect, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ProcessStepBar } from "../../../../common/layout/v2/ProcessStepBar";
import { EventHeader } from "./layout/EventHeader";
import { MobileStepDrawer } from "./layout/MobileStepDrawer";
import { AwardCriteriaStep } from "./steps/AwardCriteriaStep";
import { ClassificationStep } from "./steps/ClassificationStep";
import { DocumentsStep } from "./steps/DocumentsStep";
import { InfoStep } from "./steps/InfoStep";
import { ProcedureStep } from "./steps/ProcedureStep";
import { QualificationStep } from "./steps/QualificationStep";
import { SummaryStep } from "./steps/SummaryStep";
import {
  bffApi,
  useGetSourcingEventReadinessQuery,
  useUpdateSourcingEventMutation,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../autogen/bff-api";
import { useStepNavigation } from "./hooks/useStepNavigation";
import {
  WIZARD_STEPS,
  WIZARD_STEP_KEYS,
  stepNeighbors,
  useStepLabel,
  useStepShortLabel,
  type PublicEventStep,
} from "./wizardSteps";
import {
  useApiErrorEditorConfig,
  useCreateEditor,
  useEntityState,
} from "../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { saveWithVersionConflictRetry } from "./saveWithVersionConflictRetry";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  refetch: () => void;
}

export const EditPublicSourcingEventForm = ({ eventId, event, refetch }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const editorErrorConfig = useApiErrorEditorConfig();
  const { triggerSoft } = useApiError();
  const [updateEvent] = useUpdateSourcingEventMutation();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const { currentStep, navigateToStep } = useStepNavigation(eventId);
  const stepLabel = useStepLabel();
  const stepShortLabel = useStepShortLabel();

  // Subscribed at the orchestrator level so the readiness cache survives
  // step navigation; sections re-subscribe via useReadinessByField and RTK
  // Query dedupes. Surface fetch failures via the soft toast — without this
  // the badges silently disappear and SummaryStep's Publish gate engages
  // with no user-visible reason.
  const { error: readinessError } = useGetSourcingEventReadinessQuery({ eventId });
  useEffect(() => {
    if (readinessError) triggerSoft(readinessError);
  }, [readinessError, triggerSoft]);

  const config = useMemo(
    () => ({
      initialState: event,
      save: async (fields: Partial<SourcingEventV2PatchDto>) =>
        await saveWithVersionConflictRetry({
          fields,
          save: async (retryFields) =>
            await updateEvent({
              eventId,
              sourcingEventV2PatchDto: retryFields as SourcingEventV2PatchDto,
            }).unwrap(),
          reloadLatest: async () => {
            await dispatch(
              bffApi.endpoints.getSourcingEvent.initiate(
                { eventId },
                { forceRefetch: true },
              ),
            ).unwrap();
          },
        }),
      ...editorErrorConfig,
    }),
    [dispatch, event, eventId, updateEvent, editorErrorConfig],
  );

  useCreateEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId, config);

  const optimisticEvent = useEntityState<SourcingEventV2Dto>(eventId) ?? event;

  if (!currentStep) return <div>{t("Loading")}</div>;

  const goTo = (step: PublicEventStep | undefined): (() => void) | undefined =>
    step ? () => navigateToStep(step) : undefined;
  const labelFor = (step: PublicEventStep | undefined): string | undefined =>
    step ? stepLabel(step) : undefined;
  const navPropsFor = (step: PublicEventStep) => {
    const { previous, next } = stepNeighbors(step);
    return { previousStep: goTo(previous), nextStep: goTo(next), nextStepLabel: labelFor(next) };
  };

  const stepRenderers: Record<PublicEventStep, () => ReactNode> = {
    info: () => {
      const { nextStep, nextStepLabel } = navPropsFor("info");
      return <InfoStep eventId={eventId} nextStep={nextStep} nextStepLabel={nextStepLabel} />;
    },
    classification: () => <ClassificationStep eventId={eventId} {...navPropsFor("classification")} />,
    procedure: () => <ProcedureStep eventId={eventId} {...navPropsFor("procedure")} />,
    qualification: () => (
      <QualificationStep
        eventId={eventId}
        isActive={currentStep === "qualification"}
        {...navPropsFor("qualification")}
      />
    ),
    awardCriteria: () => (
      <AwardCriteriaStep
        eventId={eventId}
        isActive={currentStep === "awardCriteria"}
        {...navPropsFor("awardCriteria")}
      />
    ),
    documents: () => (
      <DocumentsStep
        eventId={eventId}
        event={event}
        refetch={refetch}
        isActive={currentStep === "documents"}
        {...navPropsFor("documents")}
      />
    ),
    summary: () => (
      <SummaryStep
        eventId={eventId}
        organization={selectedOrg}
        currentEditor={me}
        responsibleBuyer={selectedOrg.owner}
        previousStep={navPropsFor("summary").previousStep}
        navigateToStep={navigateToStep}
      />
    ),
  };

  const currentIndex = WIZARD_STEP_KEYS.indexOf(currentStep);

  return (
    <Box width="full" maxWidth="full">
      <MobileStepDrawer currentStep={currentStep} navigateToStep={navigateToStep} />
      <EventHeader title={optimisticEvent.eventData.title} modifiedAt={optimisticEvent.modifiedAt} />
      <Box display={{ base: "none", md: "block" }} py="8" overflowX="auto">
        <ProcessStepBar
          maxW="full"
          steps={WIZARD_STEPS.map((s) => ({ key: s.key, label: stepShortLabel(s.key) }))}
          currentKey={currentStep}
          onStepClick={(key) => navigateToStep(key as PublicEventStep)}
        />
      </Box>
      <Text display={{ base: "block", md: "none" }} py="4" fontSize="sm" fontWeight="semibold" color="smPrimary">
        {t("Step")} {currentIndex + 1}/{WIZARD_STEP_KEYS.length} · {stepLabel(currentStep)}
      </Text>
      <Box maxWidth="full" overflowX="hidden" pb={{ base: "20", md: "0" }}>
        {WIZARD_STEPS.map(({ key }) => (
          <Box key={key} display={currentStep === key ? undefined : "none"}>
            {stepRenderers[key]()}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
